/**
 * Pakiet vertykalny `pharma` — butelki, sloiki, blistry, kartoniki
 * (docs/VERTICALS.md § PharmaParams).
 *
 * To jest wyroznik produktu: zdecydowana wiekszosc parametrow jest TWARDA, bo
 * dzial regulacyjny klienta odrzuci asset za etykiete, claim, kod serii albo
 * dodana pieczatke certyfikatu.
 *
 * Listy wartosci, schemat Zoda i BRAMKA `dosageDepiction` mieszkaja w
 * `./pharma-params` (lisc bez zaleznosci — patrz komentarz tam) i sa STAD
 * RE-EKSPORTOWANE: konsumenci importuja z `./pharma`.
 *
 * TODO(spec): `PARAM_CONTROLS` w `registry.ts` nie ma wartosci `group`, a
 * `glareControl`, `blisterOptics`, `glassHandling`, `plasticHandling`,
 * `cartonOptics` i `audienceRestrictions` sa obiektami z kilkoma podpolami.
 * Uzywam `control: 'multi'` w znaczeniu "podpanel z grupa pol" — do
 * potwierdzenia albo do rozszerzenia listy kontrolek.
 *
 * Determinizm: zero `Date`, `Math.random()`, zero iteracji po `Object.keys`
 * bez sortowania, liczby przez `formatDecimal` / `formatDegrees` / `formatPercent`.
 */

import {
  QA_DEFAULT_TIERS,
  registerVerticalPack,
  type CaptionGuardrails,
  type ParamDescriptor,
  type QaCheck,
  type VerticalPack,
} from './registry';
import {
  asBoolean,
  asEnumList,
  asEnumValue,
  asNumberValue,
  formatDecimal,
  formatDegrees,
  formatPercent,
  joinList,
  joinPhrases,
  pickSliderBand,
} from './common';
import {
  PHARMA_BATCH_CODE_AREAS,
  PHARMA_CERTIFICATION_BADGES,
  PHARMA_CLAIM_GATED_PROPS,
  PHARMA_DOSAGE_DEPICTIONS,
  PHARMA_HYGIENE_CUES,
  PHARMA_LIFESTYLE_WORLDS,
  PHARMA_INGREDIENT_PROPS,
  PHARMA_LABEL_LEGIBILITIES,
  PHARMA_LABEL_POLICIES,
  PHARMA_MULTI_PACKS,
  PHARMA_RX_OTC_CLASSES,
  PHARMA_SHELF_CONTEXTS,
  PHARMA_STYLING_WORLDS,
  PHARMA_SURFACE_PRESETS,
  PHARMA_TRANSPARENCY_HANDLINGS,
  PharmaAudienceRestrictionsSchema,
  PharmaBlisterOpticsSchema,
  PharmaCartonOpticsSchema,
  PharmaGlareControlSchema,
  PharmaGlassHandlingSchema,
  PharmaLanguageLockSchema,
  PharmaParamsSchema,
  PharmaPlasticHandlingSchema,
  type PharmaAudienceRestrictions,
  type PharmaBlisterOptics,
  type PharmaCartonPanels,
  type PharmaFoilSheen,
  type PharmaGlareControl,
  type PharmaHygieneCue,
  type PharmaIngredientProp,
  type PharmaLanguageLock,
  type PharmaMultiPack,
  type PharmaRxOtcClass,
  type PharmaShelfContext,
  type PharmaSoftboxSize,
  type PharmaStripSpecular,
  type PharmaStylingWorld,
  type PharmaSurfacePreset,
} from './pharma-params';

export * from './pharma-params';

/** `generations.template_version` dla tego pakietu. */
export const PHARMA_TEMPLATE_VERSION = 'pharma@1';

// ─────────────────────────────────────────────────────────────────────────────
// Tabele fraz (to jest miejsce iterowane przy poprawianiu jakosci promptow)
// ─────────────────────────────────────────────────────────────────────────────

export const PHARMA_SURFACE_PRESET_PHRASES: Readonly<Record<PharmaSurfacePreset, string>> = {
  seamless_white_sweep: 'a seamless white paper sweep',
  matte_acrylic: 'a matte acrylic sheet, perfectly even and dust-free',
  frosted_glass: 'a frosted glass panel, softly translucent',
  travertine: 'a travertine slab with soft pitted texture',
  marble: 'a honed marble slab with fine grey veining',
  brushed_steel: 'a brushed steel plate with fine directional grain',
  linen: 'a natural linen cloth with a soft irregular weave',
  slate: 'a dark riven slate tile',
  water_surface: 'a shallow film of still water',
  bamboo: 'pale bamboo slats with visible fibrous grain',
  pill_organizer_tray: 'a clean pill organiser tray',
};

export const PHARMA_INGREDIENT_PROP_PHRASES: Readonly<Record<PharmaIngredientProp, string>> = {
  botanical_sprig: 'a fresh botanical sprig',
  citrus_slice: 'a citrus slice',
  turmeric_root: 'a piece of turmeric root',
  collagen_scoop: 'a levelled powder scoop',
  omega_capsule: 'a single translucent capsule',
  water_droplets: 'fine water droplets',
  ice: 'a few clear ice cubes',
  honey_drizzle: 'a thin honey drizzle',
  oat_grains: 'a scatter of oat grains',
  fresh_berries: 'a few fresh berries',
  mortar_pestle: 'a stone mortar and pestle',
  measuring_cup: 'a graduated measuring cup',
  glass_of_water: 'a clear glass of water',
};

const CARTON_PANEL_PHRASES: Readonly<Record<PharmaCartonPanels, string>> = {
  front: 'only the front panel visible',
  front_side: 'the front and one side panel visible',
  front_side_top: 'the front, one side and the top panel visible',
};

// ─────────────────────────────────────────────────────────────────────────────
// Deskryptory — regulacje i wiernosc
// ─────────────────────────────────────────────────────────────────────────────

/** Klauzula `locked_exact` — DOSLOWNIE z docs/VERTICALS.md. Nie przeredagowywac. */
export const PHARMA_LABEL_LOCKED_EXACT_CLAUSE =
  'Reproduce the label artwork exactly as in Reference 1: identical typography, logo, colours, layout and language. Do not invent, translate, paraphrase, or add any text.';

const labelPolicy: ParamDescriptor = {
  label: 'Label policy',
  control: 'enum',
  options: PHARMA_LABEL_POLICIES,
  constraintClass: 'hard',
  promptFragment: (value) => {
    const policy = asEnumValue(PHARMA_LABEL_POLICIES, value) ?? 'locked_exact';
    if (policy === 'locked_exact') return PHARMA_LABEL_LOCKED_EXACT_CLAUSE;
    if (policy === 'locked_partial_crop') {
      return 'The label is partially cropped by the frame; every visible part of the artwork must match Reference 1 exactly. Do not invent, translate, paraphrase, or add any text.';
    }
    return 'The label is rendered as an unreadable generic blur; no legible text, logo or claim may appear on it.';
  },
  forbidFragments: (value) => {
    const policy = asEnumValue(PHARMA_LABEL_POLICIES, value) ?? 'locked_exact';
    const shared = ['invented label text', 'translated or paraphrased label text', 'altered label layout or colours'];
    return policy === 'blurred_generic' ? [...shared, 'legible text anywhere on the label'] : shared;
  },
  conflictsWith: ['labelLegibility'],
  conflicts: (value, params) => {
    const policy = asEnumValue(PHARMA_LABEL_POLICIES, value);
    if (policy !== 'blurred_generic') return [];
    const legibility = asEnumValue(PHARMA_LABEL_LEGIBILITIES, params.labelLegibility);
    if (legibility === null || legibility === 'not_required') return [];
    return [
      {
        with: 'labelLegibility',
        message: `label is blurred to a generic surface, but legibility requires "${legibility}"`,
      },
    ];
  },
};

const labelLegibility: ParamDescriptor = {
  label: 'Label legibility',
  control: 'enum',
  options: PHARMA_LABEL_LEGIBILITIES,
  constraintClass: 'hard',
  promptFragment: (value) => {
    const legibility = asEnumValue(PHARMA_LABEL_LEGIBILITIES, value) ?? 'must_be_fully_legible';
    if (legibility === 'must_be_fully_legible') {
      return 'All label text must be fully legible and in sharp focus, including the small print.';
    }
    if (legibility === 'primary_panel_legible') {
      return 'The primary display panel of the label must be fully legible and in sharp focus.';
    }
    return 'Label legibility is not required for this shot, but no text may be altered.';
  },
  forbidFragments: (value) => {
    const legibility = asEnumValue(PHARMA_LABEL_LEGIBILITIES, value) ?? 'must_be_fully_legible';
    return legibility === 'not_required'
      ? []
      : ['blurred, smeared or motion-blurred label text', 'label text obscured by a highlight, prop or shadow'];
  },
  conflictsWith: ['labelPolicy'],
};

/** Elementy zakazane przez `claimGuardrails` — DOSLOWNIE lista z VERTICALS.md. */
export const PHARMA_CLAIM_FORBIDDEN: readonly string[] = [
  'stethoscope',
  'white lab coat or medical scrubs',
  'medical chart or patient file',
  'IV drip or infusion bag',
  'before-and-after comparison',
  'doctor, nurse or pharmacist figure',
  'any visual suggesting efficacy, diagnosis or treatment',
];

const claimGuardrails: ParamDescriptor = {
  label: 'Claim guardrails',
  control: 'bool',
  constraintClass: 'hard',
  /** Domyslnie ON: `undefined` traktujemy jak `true`. */
  promptFragment: (value) => {
    if (asBoolean(value) === false) return null;
    return 'Nothing in the image may suggest efficacy, diagnosis or treatment: no clinical or medical setting, no medical professionals, no medical equipment, no before-and-after comparison.';
  },
  forbidFragments: (value) => (asBoolean(value) === false ? [] : PHARMA_CLAIM_FORBIDDEN),
};

const batchCodeArea: ParamDescriptor = {
  label: 'Batch / expiry code area',
  control: 'enum',
  options: PHARMA_BATCH_CODE_AREAS,
  constraintClass: 'hard',
  promptFragment: (value) => {
    const area = asEnumValue(PHARMA_BATCH_CODE_AREAS, value) ?? 'keep_blank';
    if (area === 'keep_blank') {
      return 'Leave the batch/expiry code area blank; never render fake lot numbers or dates.';
    }
    return 'Reproduce the batch/expiry code exactly as printed in Reference 1; never invent digits or dates.';
  },
  forbidFragments: () => ['invented lot number', 'invented expiry or manufacturing date', 'garbled code characters'],
};

const certificationBadges: ParamDescriptor = {
  label: 'Certification badges',
  control: 'enum',
  options: PHARMA_CERTIFICATION_BADGES,
  constraintClass: 'hard',
  promptFragment: () =>
    'Do not add certification, organic, or approval seals that are not present in Reference 1.',
  forbidFragments: () => [
    'invented certification seal',
    'invented organic or eco badge',
    'invented regulatory approval mark',
    'invented award badge',
  ],
};

const dosageDepiction: ParamDescriptor = {
  label: 'Dosage depiction',
  control: 'enum',
  options: PHARMA_DOSAGE_DEPICTIONS,
  constraintClass: 'hard',
  /**
   * Bramka jest w `PharmaParamsSchema`, wiec tutaj wartosc jest JUZ
   * zatwierdzona regulacyjnie. Fragment nigdy nie wymienia nazwiska
   * recenzenta — `regulatoryReviewedBy` nie idzie do modelu.
   */
  promptFragment: (value) => {
    const depiction = asEnumValue(PHARMA_DOSAGE_DEPICTIONS, value) ?? 'none';
    if (depiction === 'none') {
      return 'No loose dose units are shown outside the packaging.';
    }
    if (depiction === 'single_unit') {
      return 'Exactly one dose unit is shown beside the packaging, clean and undamaged.';
    }
    if (depiction === 'measured_scoop') {
      return 'One levelled measured scoop of the product is shown beside the packaging.';
    }
    if (depiction === 'in_palm') {
      return 'A single dose unit rests in an open palm; no mouth, tongue or act of taking the product is shown.';
    }
    return 'A few dose units are scattered beside the packaging, evenly spaced and undamaged.';
  },
  forbidFragments: (value) => {
    const depiction = asEnumValue(PHARMA_DOSAGE_DEPICTIONS, value) ?? 'none';
    const shared = [
      'a person swallowing or about to swallow the product',
      'a dose unit near or inside a mouth',
      'crushed, broken or split dose units',
    ];
    return depiction === 'none' ? [...shared, 'loose dose units outside the packaging'] : shared;
  },
  conflictsWith: ['hygieneCue'],
  conflicts: (value, params) => {
    const depiction = asEnumValue(PHARMA_DOSAGE_DEPICTIONS, value);
    if (depiction === null || depiction === 'none') return [];
    // Dawki poza opakowaniem wykluczaja opakowanie nadal zafoliowane.
    const hygiene = asEnumValue(PHARMA_HYGIENE_CUES, params.hygieneCue);
    if (hygiene !== 'sealed_cellophane') return [];
    return [
      {
        with: 'hygieneCue',
        message: `dose units are shown ("${depiction}") while the pack is still sealed in cellophane`,
      },
    ];
  },
};

/**
 * `regulatoryReviewedBy` ma deskryptor, bo panel UI renderuje sie z
 * `paramDescriptors` i bez wpisu operator nie mialby gdzie wpisac podpisu.
 * `promptFragment` ZAWSZE zwraca `null`: nazwisko recenzenta nie jest danymi
 * obrazu i nie moze wyciec do modelu ani do `prompt_text`.
 */
const regulatoryReviewedBy: ParamDescriptor = {
  label: 'Regulatory sign-off (required for dosage depiction)',
  control: 'text',
  constraintClass: 'soft',
  promptFragment: () => null,
};

const audienceRestrictions: ParamDescriptor = {
  label: 'Audience restrictions',
  control: 'multi',
  constraintClass: 'hard',
  promptFragment: (value) => {
    const parsed = PharmaAudienceRestrictionsSchema.safeParse(value);
    const restrictions: PharmaAudienceRestrictions = parsed.success ? parsed.data : {};
    const consented = restrictions.explicitConsentRefId !== undefined;
    const parts = [
      restrictions.noMinors === false && consented ? null : 'no children or minors may appear in the image',
      restrictions.noPregnancyCues === false && consented ? null : 'nothing may suggest pregnancy',
      restrictions.noVulnerableGroups === false && consented
        ? null
        : 'no depiction of patients or other vulnerable groups',
    ];
    return joinPhrases(parts, '; ');
  },
  forbidFragments: () => ['child or infant in frame', 'pregnant figure or pregnancy cue', 'visibly ill person'],
};

const languageLock: ParamDescriptor = {
  label: 'Language lock',
  control: 'bool',
  constraintClass: 'hard',
  promptFragment: (value) => {
    const parsed = PharmaLanguageLockSchema.safeParse(value);
    const lock: PharmaLanguageLock = parsed.success ? parsed.data : {};
    if (lock.enabled === false) return null;
    const market = lock.marketLocale === undefined ? null : `the ${lock.marketLocale} market`;
    return `All printed text must stay in the language of ${market ?? 'the market shown in Reference 1'}; do not translate or localise any wording.`;
  },
  forbidFragments: (value) => {
    const parsed = PharmaLanguageLockSchema.safeParse(value);
    const lock: PharmaLanguageLock = parsed.success ? parsed.data : {};
    return lock.enabled === false ? [] : ['translated label text', 'mixed-language label text'];
  },
};

const rxOtcClass: ParamDescriptor = {
  label: 'Regulatory class',
  control: 'enum',
  options: PHARMA_RX_OTC_CLASSES,
  constraintClass: 'hard',
  /** Rx jest najostrzejsza: zero kontekstu lifestyle (docs/VERTICALS.md). */
  promptFragment: (value) => {
    const rxClass = asEnumValue(PHARMA_RX_OTC_CLASSES, value) ?? 'otc';
    if (rxClass === 'rx') {
      return 'This is a prescription product: the image must stay a neutral studio packshot with no lifestyle context, no consumer setting and no person implied as a user.';
    }
    if (rxClass === 'otc') {
      return 'This is an over-the-counter medicine: the image may show a neutral everyday context but must not imply a therapeutic outcome.';
    }
    if (rxClass === 'supplement') {
      return 'This is a food supplement: the image must not present the product as a medicine or as a substitute for a varied diet.';
    }
    if (rxClass === 'cosmetic_derma') {
      return 'This is a dermocosmetic product: the image may show skincare context but must not imply a medical treatment.';
    }
    return 'This is a medical device: the image must show the device neutrally, with no implied clinical outcome.';
  },
  forbidFragments: (value) => {
    const rxClass = asEnumValue(PHARMA_RX_OTC_CLASSES, value) ?? 'otc';
    const shared = ['implied therapeutic outcome', 'implied medical endorsement'];
    return rxClass === 'rx'
      ? [...shared, 'lifestyle or home setting', 'person implied as a patient or user', 'food or drink in frame']
      : shared;
  },
  conflictsWith: ['stylingWorld'],
  conflicts: (value, params) => {
    const rxClass = asEnumValue(PHARMA_RX_OTC_CLASSES, value);
    if (rxClass !== 'rx') return [];
    const world = asEnumValue(PHARMA_STYLING_WORLDS, params.stylingWorld);
    if (world === null || !PHARMA_LIFESTYLE_WORLDS.includes(world)) return [];
    return [
      {
        with: 'stylingWorld',
        message: `prescription product must not sit in a lifestyle context, but styling world is "${world}"`,
      },
    ];
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Deskryptory — optyka i odblaski
// ─────────────────────────────────────────────────────────────────────────────

/** Twarda klauzula odblaskow — DOSLOWNIE z docs/VERTICALS.md. */
export const PHARMA_GLARE_HARD_CLAUSE =
  'No blown-out specular highlight across the label; no reflection obscuring any printed text.';

const SOFTBOX_SIZE_PHRASES: Readonly<Record<PharmaSoftboxSize, string>> = {
  small: 'a small softbox for a tighter, more defined highlight',
  medium: 'a medium softbox',
  large: 'a large softbox for a broad, gentle highlight',
  wall: 'a full wall of diffusion for an almost shadowless wrap',
};

const STRIP_SPECULAR_PHRASES: Readonly<Record<PharmaStripSpecular, string>> = {
  none: 'no strip highlights on the container edges',
  single_broad: 'one broad specular strip along the container edge',
  dual_edge: 'two symmetrical specular strips along both container edges',
  wrap: 'a specular strip wrapping around the container silhouette',
};

const glareControl: ParamDescriptor = {
  label: 'Glare control',
  control: 'multi',
  constraintClass: 'hard',
  /**
   * Parametr `H + S` (docs/VERTICALS.md): twarda klauzula o etykiecie plus
   * miekki opis rigu. Kontrakt `ParamDescriptor` ma jedna klase, wiec caly
   * fragment idzie do `CONSTRAINTS` — imperatywne brzmienie utrzymane.
   * TODO(spec): czy potrzebna jest klasa `mixed` z dwoma fragmentami?
   */
  promptFragment: (value) => {
    const parsed = PharmaGlareControlSchema.safeParse(value);
    const glare: PharmaGlareControl = parsed.success ? parsed.data : {};
    const hard = glare.labelHotspotForbidden === false ? null : PHARMA_GLARE_HARD_CLAUSE;
    const softbox = glare.softboxSize === undefined ? null : `Light the packaging with ${SOFTBOX_SIZE_PHRASES[glare.softboxSize]}`;
    const strip = glare.stripSpecular === undefined ? null : STRIP_SPECULAR_PHRASES[glare.stripSpecular];
    const cap =
      glare.capReflectionSuppression === true
        ? 'Suppress reflections on the cap so it reads as material, not as a mirror'
        : null;
    return joinPhrases([hard, softbox, strip, cap], '; ');
  },
  forbidFragments: (value) => {
    const parsed = PharmaGlareControlSchema.safeParse(value);
    const glare: PharmaGlareControl = parsed.success ? parsed.data : {};
    return glare.labelHotspotForbidden === false
      ? []
      : ['blown-out specular hotspot on the label', 'reflection covering printed text', 'clipped white highlights'];
  },
};

/** Twarda klauzula blistra — DOSLOWNIE z docs/VERTICALS.md. */
export const PHARMA_BLISTER_HARD_CLAUSE =
  'Aluminium foil reads as brushed metal, not a mirror; no rainbow moiré.';

const FOIL_SHEEN_PHRASES: Readonly<Record<PharmaFoilSheen, string>> = {
  brushed: 'brushed aluminium with a fine directional grain',
  satin: 'satin aluminium with a soft even sheen',
  mirror: 'mirror-bright aluminium',
};

const blisterOptics: ParamDescriptor = {
  label: 'Blister optics',
  control: 'multi',
  constraintClass: 'hard',
  promptFragment: (value) => {
    const parsed = PharmaBlisterOpticsSchema.safeParse(value);
    const optics: PharmaBlisterOptics = parsed.success ? parsed.data : {};
    const sheen = optics.foilSheen === undefined ? null : `The foil backing reads as ${FOIL_SHEEN_PHRASES[optics.foilSheen]}`;
    const cavities =
      optics.cavityCount === undefined
        ? null
        : `The blister has exactly ${formatDecimal(optics.cavityCount)} cavities, evenly spaced`;
    const fill =
      optics.cavityFillVisible === undefined
        ? null
        : optics.cavityFillVisible
          ? 'The dose units are visible through the formed cavities'
          : 'The cavities are sealed and their contents are not visible';
    const angle =
      optics.foilAngleDeg === undefined ? null : `The foil grain runs at ${formatDegrees(optics.foilAngleDeg)}`;
    return joinPhrases([PHARMA_BLISTER_HARD_CLAUSE, sheen, cavities, fill, angle], '; ');
  },
  forbidFragments: (value) => {
    const parsed = PharmaBlisterOpticsSchema.safeParse(value);
    const optics: PharmaBlisterOptics = parsed.success ? parsed.data : {};
    const shared = ['mirror-like foil reflection', 'rainbow moiré on the foil', 'crumpled or torn foil'];
    return optics.avoidMoire === false ? ['mirror-like foil reflection', 'crumpled or torn foil'] : shared;
  },
};

const glassHandling: ParamDescriptor = {
  label: 'Glass handling',
  control: 'multi',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const parsed = PharmaGlassHandlingSchema.safeParse(value);
    if (!parsed.success) return null;
    const glass = parsed.data;
    const edge =
      glass.edgeDefinition === undefined
        ? null
        : glass.edgeDefinition === 'crisp'
          ? 'crisp, precisely defined glass edges'
          : 'softly defined glass edges';
    const refraction =
      glass.refractionStrength === undefined
        ? null
        : `refraction through the glass at ${formatDecimal(glass.refractionStrength)} strength`;
    const meniscus =
      glass.meniscusVisible === undefined
        ? null
        : glass.meniscusVisible
          ? 'a visible meniscus at the liquid surface'
          : 'no visible meniscus';
    const level =
      glass.liquidLevelPct === undefined ? null : `liquid filled to ${formatPercent(glass.liquidLevelPct)} of the bottle`;
    const color = glass.liquidColorHex === undefined ? null : `liquid colour ${glass.liquidColorHex}`;
    const dropper =
      glass.dropperPosition === undefined
        ? null
        : glass.dropperPosition === 'in'
          ? 'the dropper seated in the neck of the bottle'
          : glass.dropperPosition === 'beside'
            ? 'the dropper resting beside the bottle'
            : 'the dropper held above the bottle with a single drop forming';
    const parts = joinPhrases([edge, refraction, meniscus, level, color, dropper], ', ');
    return parts === null ? null : `Render the glass with ${parts}`;
  },
};

const plasticHandling: ParamDescriptor = {
  label: 'Plastic handling',
  control: 'multi',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const parsed = PharmaPlasticHandlingSchema.safeParse(value);
    if (!parsed.success) return null;
    const plastic = parsed.data;
    const opacity =
      plastic.hdpeOpacity === undefined
        ? null
        : `HDPE walls at ${formatDecimal(plastic.hdpeOpacity)} opacity, reading as moulded plastic rather than glass`;
    const seal =
      plastic.tamperSealVisible === undefined
        ? null
        : plastic.tamperSealVisible
          ? 'the tamper-evident seal intact and visible'
          : 'no tamper seal in view';
    const cap = plastic.ribbedCapDetail === true ? 'the cap ribbing crisply resolved' : null;
    const parts = joinPhrases([opacity, seal, cap], ', ');
    return parts === null ? null : `Render the plastic container with ${parts}`;
  },
  conflictsWith: ['transparencyHandling'],
};

const cartonOptics: ParamDescriptor = {
  label: 'Carton optics',
  control: 'multi',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const parsed = PharmaCartonOpticsSchema.safeParse(value);
    if (!parsed.success) return null;
    const carton = parsed.data;
    const edges =
      carton.edgeCrispness === undefined
        ? null
        : `carton edges and folds at ${formatDecimal(carton.edgeCrispness)} crispness, straight and undented`;
    const emboss = carton.embossVisible === true ? 'the embossed detail catching a raking light' : null;
    const spotUv = carton.spotUvGloss === true ? 'spot UV gloss areas reading against the matte stock' : null;
    const foil =
      carton.foilStampSheen === undefined ? null : `foil stamping at ${formatDecimal(carton.foilStampSheen)} sheen`;
    const panels =
      carton.panelsVisible === undefined
        ? null
        : CARTON_PANEL_PHRASES[carton.panelsVisible];
    const parts = joinPhrases([edges, emboss, spotUv, foil, panels], ', ');
    return parts === null ? null : `Render the carton with ${parts}`;
  },
};

const transparencyHandling: ParamDescriptor = {
  label: 'Transparency',
  control: 'enum',
  options: PHARMA_TRANSPARENCY_HANDLINGS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_TRANSPARENCY_HANDLINGS, value);
    if (key === null) return null;
    if (key === 'opaque') return 'The container is fully opaque; the contents are not visible';
    if (key === 'translucent') return 'The container is translucent, hinting at the contents without revealing them';
    return 'The container is clear and the contents are plainly visible through it';
  },
  conflictsWith: ['plasticHandling'],
  conflicts: (value, params) => {
    const key = asEnumValue(PHARMA_TRANSPARENCY_HANDLINGS, value);
    if (key === null) return [];
    const parsed = PharmaPlasticHandlingSchema.safeParse(params.plasticHandling);
    if (!parsed.success) return [];
    const opacity = parsed.data.hdpeOpacity;
    if (opacity === undefined) return [];

    // hdpeOpacity: 0 = przezroczyste, 1 = calkowicie kryjace.
    if (key === 'clear_with_contents' && opacity > 0.5) {
      return [
        {
          with: 'plasticHandling',
          message: `contents must be visible, but hdpeOpacity ${opacity.toFixed(2)} makes the wall opaque`,
        },
      ];
    }
    if (key === 'opaque' && opacity < 0.5) {
      return [
        {
          with: 'plasticHandling',
          message: `container must be opaque, but hdpeOpacity ${opacity.toFixed(2)} lets the contents show`,
        },
      ];
    }
    return [];
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Deskryptory — styling
// ─────────────────────────────────────────────────────────────────────────────

const STYLING_WORLD_PHRASES: Readonly<Record<PharmaStylingWorld, string>> = {
  clinical_white: 'a clean clinical white studio world, cool neutrals and no decoration',
  pharmacy_shelf: 'a tidy pharmacy shelf world with shallow depth',
  laboratory_clean: 'a spotless laboratory world, glass and stainless steel',
  wellness_natural: 'a warm natural wellness world with soft daylight',
  apothecary_botanical: 'an apothecary world with dried botanicals and aged wood',
  spa_calm: 'a calm spa world, stone, water and diffuse light',
  sport_performance: 'a sport performance world, graphite tones and hard light',
  luxury_derma: 'a luxury dermocosmetic world, polished surfaces and restrained palette',
  kitchen_daily_ritual: 'a daily-ritual kitchen counter world, honest morning light',
};

const stylingWorld: ParamDescriptor = {
  label: 'Styling world',
  control: 'enum',
  options: PHARMA_STYLING_WORLDS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_STYLING_WORLDS, value);
    return key === null ? null : `Set the shot in ${STYLING_WORLD_PHRASES[key]}`;
  },
  /** Predykat wartosciowy jest po stronie `rxOtcClass` (parametr twardy). */
  conflictsWith: ['rxOtcClass'],
};

const surfacePreset: ParamDescriptor = {
  label: 'Surface preset',
  control: 'enum',
  options: PHARMA_SURFACE_PRESETS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_SURFACE_PRESETS, value);
    return key === null ? null : `The packaging stands on ${PHARMA_SURFACE_PRESET_PHRASES[key]}`;
  },
};

const ingredientProps: ParamDescriptor = {
  label: 'Ingredient props',
  control: 'multi',
  options: PHARMA_INGREDIENT_PROPS,
  constraintClass: 'soft',
  /**
   * Bramkowanie przez `claimGuardrails`: propsy sugerujace dawkowanie albo
   * przygotowanie preparatu wypadaja z promptu, gdy guardraile sa wlaczone
   * (a domyslnie SA — `undefined` znaczy wlaczone).
   */
  promptFragment: (value, ctx) => {
    const keys = asEnumList(PHARMA_INGREDIENT_PROPS, value);
    if (keys === null || keys.length === 0) return null;
    const guardrailsOn = asBoolean(ctx.params['claimGuardrails']) !== false;
    const allowed = guardrailsOn ? keys.filter((key) => !PHARMA_CLAIM_GATED_PROPS.includes(key)) : keys;
    if (allowed.length === 0) return null;
    return `Ingredient cues in frame: ${joinList(allowed.map((key) => PHARMA_INGREDIENT_PROP_PHRASES[key]))}, clearly decorative and secondary to the packaging`;
  },
  forbidFragments: (value, ctx) => {
    const keys = asEnumList(PHARMA_INGREDIENT_PROPS, value);
    if (keys === null) return [];
    const guardrailsOn = asBoolean(ctx.params['claimGuardrails']) !== false;
    if (!guardrailsOn) return [];
    return keys
      .filter((key) => PHARMA_CLAIM_GATED_PROPS.includes(key))
      .map((key) => PHARMA_INGREDIENT_PROP_PHRASES[key]);
  },
};

/** Twarda klauzula spojnosci multipacka — DOSLOWNIE z docs/VERTICALS.md. */
export const PHARMA_MULTIPACK_HARD_CLAUSE =
  'All units show identical label artwork and orientation; no variation between units.';

const MULTI_PACK_PHRASES: Readonly<Record<PharmaMultiPack, string>> = {
  single: 'a single unit in frame',
  duo: 'two identical units side by side',
  trio_stepped: 'three identical units in a stepped arrangement',
  family_lineup: 'the full family lined up side by side',
  box_plus_blister: 'the carton with one blister leaning against it',
  box_plus_bottle: 'the carton with the bottle standing beside it',
};

const multiPack: ParamDescriptor = {
  label: 'Multi-pack',
  control: 'enum',
  options: PHARMA_MULTI_PACKS,
  constraintClass: 'hard',
  /** Parametr `S + H`: opis ukladu + twarda klauzula spojnosci etykiet. */
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_MULTI_PACKS, value) ?? 'single';
    if (key === 'single') return MULTI_PACK_PHRASES[key];
    return `Show ${MULTI_PACK_PHRASES[key]}. ${PHARMA_MULTIPACK_HARD_CLAUSE}`;
  },
  forbidFragments: (value) => {
    const key = asEnumValue(PHARMA_MULTI_PACKS, value) ?? 'single';
    return key === 'single'
      ? []
      : ['units with differing label artwork', 'units rotated inconsistently', 'duplicated unit with warped label'];
  },
};

const HYGIENE_CUE_PHRASES: Readonly<Record<PharmaHygieneCue, string>> = {
  sterile_pristine: 'the packaging is factory-fresh and spotless, sealed and untouched',
  in_use_open: 'the packaging is open and in use, still clean',
  sealed_cellophane: 'the packaging is still wrapped in taut cellophane',
};

const hygieneCue: ParamDescriptor = {
  label: 'Hygiene cue',
  control: 'enum',
  options: PHARMA_HYGIENE_CUES,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_HYGIENE_CUES, value);
    return key === null ? null : HYGIENE_CUE_PHRASES[key];
  },
  conflictsWith: ['dosageDepiction'],
};

const SHELF_CONTEXT_PHRASES: Readonly<Record<PharmaShelfContext, string>> = {
  isolated: 'the packaging isolated with no surrounding context',
  single_shelf: 'the packaging on a single clean shelf',
  pharmacy_shelf_bokeh: 'the packaging in front of a pharmacy shelf dissolved into bokeh',
  bathroom_counter: 'the packaging on a tidy bathroom counter',
  gym_bag: 'the packaging beside an open gym bag',
};

const shelfContext: ParamDescriptor = {
  label: 'Shelf context',
  control: 'enum',
  options: PHARMA_SHELF_CONTEXTS,
  constraintClass: 'soft',
  promptFragment: (value) => {
    const key = asEnumValue(PHARMA_SHELF_CONTEXTS, value);
    return key === null ? null : `Show ${SHELF_CONTEXT_PHRASES[key]}`;
  },
};

/** Pasma "master diala" `trustSignal`: `clinical ↔ natural`. */
export const TRUST_SIGNAL_BANDS: readonly string[] = [
  'strictly clinical treatment: cool neutral palette, hard clean light, no organic props',
  'clinical treatment with a single warm accent',
  'balanced treatment: neutral palette with a hint of natural texture',
  'natural treatment: warm palette, soft daylight and organic textures',
  'fully natural treatment: warm earthy palette, botanical texture and sun-like light',
];

const trustSignal: ParamDescriptor = {
  label: 'Trust signal',
  control: 'slider',
  constraintClass: 'soft',
  promptFragment: (value) => {
    const level = asNumberValue(value);
    if (level === null) return null;
    return pickSliderBand(TRUST_SIGNAL_BANDS, level);
  },
};

export const PHARMA_PARAM_DESCRIPTORS: Readonly<Record<string, ParamDescriptor>> = {
  audienceRestrictions,
  batchCodeArea,
  blisterOptics,
  cartonOptics,
  certificationBadges,
  claimGuardrails,
  dosageDepiction,
  glareControl,
  glassHandling,
  hygieneCue,
  ingredientProps,
  labelLegibility,
  labelPolicy,
  languageLock,
  multiPack,
  plasticHandling,
  regulatoryReviewedBy,
  rxOtcClass,
  shelfContext,
  stylingWorld,
  surfacePreset,
  transparencyHandling,
  trustSignal,
};

// ─────────────────────────────────────────────────────────────────────────────
// Twarde klauzule domyslne i lista negatywna
// ─────────────────────────────────────────────────────────────────────────────

export const PHARMA_DEFAULT_CONSTRAINTS: readonly string[] = [
  'The packaging must be identical to Reference 1 in shape, proportions, artwork and colour.',
  'Every character of printed text must come from the references; do not invent, translate or paraphrase any wording.',
  'Do not add or remove any printed element, seal, symbol or pictogram.',
  'Keep the product the unambiguous subject of the frame.',
];

export const PHARMA_DEFAULT_FORBID: readonly string[] = [
  'invented or garbled text',
  'distorted or warped label artwork',
  'duplicated or mismatched packaging',
  'text overlay, caption or watermark',
  'dented, dirty or damaged packaging',
  'blown-out highlights on the label',
  'rainbow moiré or colour fringing',
  'fake batch, lot or expiry code',
];

// ─────────────────────────────────────────────────────────────────────────────
// Checki QA (docs/VERTICALS.md § Checki QA per vertykal)
// ─────────────────────────────────────────────────────────────────────────────

export const PHARMA_QA_CHECKS: readonly QaCheck[] = [
  {
    id: 'label_text_unchanged',
    label: 'Label text identical to the reference',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'blocking',
    comparesToRefRole: 'label',
    visionInstruction:
      'Compare the label in the generated image with the label reference. Return textMatches (boolean), differences (list of every wording, typography, colour or layout difference), legible (boolean) and occlusions (list of elements covering printed text).',
    /** Check dotyczy tylko polityki `locked_exact` (docs/VERTICALS.md). */
    appliesWhen: (ctx) => (asEnumValue(PHARMA_LABEL_POLICIES, ctx.params['labelPolicy']) ?? 'locked_exact') === 'locked_exact',
  },
  {
    id: 'no_forbidden_props',
    label: 'No props forbidden by the claim guardrails',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'blocking',
    visionInstruction: `List anything in the generated image matching the following forbidden set: ${PHARMA_CLAIM_FORBIDDEN.join(', ')}.`,
    appliesWhen: (ctx) => asBoolean(ctx.params['claimGuardrails']) !== false,
  },
  {
    id: 'no_invented_text',
    label: 'No text that is absent from the references',
    kind: 'vision',
    tiers: QA_DEFAULT_TIERS,
    severity: 'blocking',
    comparesToRefRole: 'label',
    visionInstruction:
      'List every piece of legible text in the generated image and flag anything that is not present in the references.',
  },
  {
    id: 'dimensions_and_sharpness',
    label: 'Output dimensions and sharpness',
    kind: 'image_info',
    tiers: QA_DEFAULT_TIERS,
    severity: 'warning',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Guardraile opisow (docs/CAPTIONS.md § Lint claimow)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Czasowniki i frazy claimowe. Kolejnosc jest ZADEKLAROWANA (bez sortowania w
 * runtime), zeby lint dawal identyczne wyniki po stronie klienta i serwera.
 * Dopasowanie jest bez znakow diakrytycznych po stronie lintera, dlatego warianty
 * podane sa w formie podstawowej.
 *
 * TODO(spec): dokumentacja mowi "i odpowiedniki w innych jezykach" — lista
 * ponizej pokrywa PL i EN. Kolejne rynki powinny dojsc jako osobna tabela per
 * `locale`, nie jako rozrost tej listy.
 */
export const PHARMA_FORBIDDEN_VERBS: readonly string[] = [
  // PL
  'leczy',
  'wyleczy',
  'leczenie',
  'uleczy',
  'zapobiega',
  'zapobieganie',
  'gwarantuje',
  'gwarantowany',
  'klinicznie dowiedzione',
  'klinicznie udowodnione',
  'klinicznie potwierdzone',
  'likwiduje',
  'usuwa przyczyne',
  'natychmiastowa ulga',
  'bez skutkow ubocznych',
  'polecany przez lekarzy',
  'terapia',
  'diagnozuje',
  // EN
  'cures',
  'cure',
  'heals',
  'heal',
  'treats',
  'treatment of',
  'prevents',
  'prevention of',
  'guarantees',
  'guaranteed',
  'clinically proven',
  'clinically shown',
  'clinically confirmed',
  'eliminates',
  'instant relief',
  'no side effects',
  'doctor recommended',
  'diagnoses',
];

/**
 * Disclaimery wg `rxOtcClass`. Teksty sa po angielsku, jak reszta stringow w
 * kodzie.
 *
 * TODO(spec): brzmienie disclaimerow jest regulowane PER RYNEK i musi byc
 * dostarczone przez dzial prawny klienta w jego jezyku. Ta tabela jest
 * placeholderem struktury, nie zatwierdzonym tekstem prawnym — docelowo
 * `presets(kind='caption')` albo osobna tabela per `locale`.
 */
export const PHARMA_DISCLAIMERS_BY_CLASS: Readonly<Record<PharmaRxOtcClass, readonly string[]>> = {
  otc: ['This is a medicine. For your safety, use it according to the package leaflet.'],
  rx: ['Prescription-only medicine. Advertising to the general public is restricted.'],
  supplement: ['Food supplement. Do not use as a substitute for a varied and balanced diet.'],
  cosmetic_derma: ['Cosmetic product. For external use only.'],
  medical_device: ['Medical device. Read the instructions for use before use.'],
};

/** Disclaimery dla klasy regulacyjnej; `undefined` traktujemy jak `otc`. */
export function pharmaRequiredDisclaimers(rxClass: PharmaRxOtcClass | undefined): readonly string[] {
  return PHARMA_DISCLAIMERS_BY_CLASS[rxClass ?? 'otc'];
}

export const PHARMA_CAPTION_GUARDRAILS: CaptionGuardrails = {
  forbiddenVerbs: PHARMA_FORBIDDEN_VERBS,
  /** Domyslny zestaw (OTC); route opisow podmienia go wg `rxOtcClass`. */
  requiredDisclaimers: PHARMA_DISCLAIMERS_BY_CLASS.otc,
};

// ─────────────────────────────────────────────────────────────────────────────
// Pakiet
// ─────────────────────────────────────────────────────────────────────────────

export const pharmaPack: VerticalPack = {
  id: 'pharma',
  templateVersion: PHARMA_TEMPLATE_VERSION,
  paramsSchema: PharmaParamsSchema,
  paramDescriptors: PHARMA_PARAM_DESCRIPTORS,
  defaultConstraints: PHARMA_DEFAULT_CONSTRAINTS,
  defaultForbid: PHARMA_DEFAULT_FORBID,
  qaChecks: PHARMA_QA_CHECKS,
  captionGuardrails: PHARMA_CAPTION_GUARDRAILS,
};

registerVerticalPack(pharmaPack);
